import { FormikErrors } from 'formik';
import { FocusEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { Logger } from '@utils/logger';
import { getCountryCode } from '@redux/selectors/verification-configuration-selectors';
import { ParsedCountry } from 'react-international-phone';
import { AXNUITelephoneInputWithErrorMessage } from '@iddataweb/idw-common-ui/containers';

type InternationalTelephoneInputProps = {
	title: string;
	attribute: AttributeFormState;
	fieldName: string;
	fieldTouched: boolean;
	fieldError: string;
	fieldIsInvalid: boolean;
	fieldShouldBeHighlighted: boolean;
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	onBlur: (event: FocusEvent<HTMLInputElement>) => void;
	onSetSubmitValue: (data: any) => void;
	isSubmitting: boolean;
};

export const InternationalTelephoneInput = (props: InternationalTelephoneInputProps) => {
	const {
		title,
		attribute,
		fieldName,
		fieldTouched,
		fieldError,
		fieldIsInvalid,
		fieldShouldBeHighlighted,
		onBlur,
		setFieldValue,
		setFieldTouched,
		onSetSubmitValue,
		isSubmitting
	} = props;

	const countryCode = useSelector(getCountryCode);
	const [firstLoad, setFirstLoad] = useState(true);
	const [phoneNumber, setPhoneNumber] = useState<{ phone: string; dialCode: string; }>({
		phone: attribute.inputData.telephone || "",
		dialCode: attribute.inputData.dialCode || ""
	});

	const setSubmitValue = (dialCode: string, inputValue: string) => {
		const inputData = {
			dialCode: dialCode,
			telephone: inputValue
		};

		Logger.info(`[InternationalTelephoneInput] Dial code submit value: ${dialCode}`);
		Logger.info(`[InternationalTelephoneInput] Phone submit value: ${inputValue}`);
		onSetSubmitValue({ inputData });
	};

	const handleChange = async (phone: string, meta: { country: ParsedCountry; inputValue: string; }) => {
		setPhoneNumber({ phone, dialCode: meta.country?.dialCode || "" });
		setSubmitValue(meta.country.dialCode, meta.inputValue);

		if (meta.inputValue && meta.country.dialCode) {
			await setFieldValue(fieldName, phone);
			setFieldTouched(fieldName, true, true);
		} else {
			await setFieldValue(fieldName, '');

			if (firstLoad) {
				setFirstLoad(false);
			} else {
				setFieldTouched(fieldName, true, true);
			}
		}
	};

	return (
		<AXNUITelephoneInputWithErrorMessage
			idPrefix={`attr${attribute.id}`}
			title={title}
			defaultCountry={countryCode ? countryCode.toLocaleLowerCase() : "us"}
			phoneNumber={phoneNumber}
			fieldName={fieldName}
			fieldTouched={fieldTouched}
			fieldError={fieldError}
			fieldIsInvalid={fieldIsInvalid}
			fieldShouldBeHighlighted={fieldShouldBeHighlighted}
			onBlur={onBlur}
			setFieldTouched={setFieldTouched}
			onChange={handleChange}
			isDisabled={attribute.isDisabled}
			isPlaceholder={attribute.isPlaceholder}
			isOptional={attribute.optional}
			isSubmitting={isSubmitting}
		/>
	);
};